import React from "react";
import jp from 'jsonpath'
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { IContextProvider, IWidgetPreloader, registerWidget } from "../../uxp";
import { IErrorProps } from "../designer/Designer";
import { IContextData } from "../../hooks/useContextData";
import { deriveJSONPath, ICollection, IDataSource, IDataSourceBinding, IModelAction, schemaAtJsonPath, TargetDataStructure } from "./data-source-picker/datasource";
import { getUrlFriendlyString, hasValue, loadScriptFileAsync } from "../../util";
export interface IBaseProperty {
}
export interface IBaseFieldProperty {
    uxpContext?: IContextProvider,
    contextData?: IContextData
}
export interface IBaseInputFieldProperty extends IBaseFieldProperty {
    value?: any,
    onChange?: (value: any) => void,
    id?: string
}
export interface IUIFieldBaseProperty {
    id?: string;
}

export interface IUIPropertyDefinition<T> {
    defaults: T;
    render: (props: IPropertyEditorProps<T>, uxpContext?: IContextProvider, contextData?: IContextData, containerId?: string, customProps?: any) => JSX.Element;
}

export interface IPropertyEditorProps<T> {
    uiProps: T;
    onChange: (newProps: T) => void;
    onChangeAny: (path: PropertyPath, value: any) => void;
    uxpContext: IContextProvider,
    contextData?: IContextData
    parentUIProps: any;
    designer: IWDDesignModeProps;
    containerId: string;
    hideFields?: string[]
}

export function canShowField(field: string, configuration: string[]) {
    if (!configuration || configuration?.length == 0) return true
    return !configuration?.includes(field)
}

export function wrapPropertyEditor<T>(editor: React.FunctionComponent<IPropertyEditorProps<T>>, defaults: T, hideFields?: string[]): IUIPropertyDefinition<T> {
    return {
        defaults, render: (props, uxpContext, contextData, containerId, customProps) => {
            return editor({ ...props, hideFields: hideFields });
        }
    };
}

export type IWidgetMode = 'preview' | 'live' | 'thumbnail';
export type IUIPropertyDefinitionMap = {
    [key: string]: (
        IUIPropertyDefinition<IBaseProperty>
        | { [subKey: string]: IUIPropertyDefinition<IBaseProperty> }
    )
};

export type PropertyPanelType = 'general' | 'properties' | 'fields' | 'templates';

export interface IWidgetProps {
    description?: string;
    name?: string;
    height?: number;
}

export declare type SourceBrowser = (uxpContext: IContextProvider, template: IWidgetTemplate, onCancel: () => void, onSelect: (source: IModelAction | ICollection) => Promise<boolean>, source?: IModelAction | ICollection, contextData?:IContextData) => React.ReactElement;
export interface IWDDesignModeProps {
    mode: IWidgetMode;
    widgetName: string;
    selectedPanel?: PropertyPanelType
    onSelect: (ref: any, props: any, data: any, id?: string) => void;
    selectedItem: any;
    setUIProps: (uiProps: any) => void;
    setMobileCardUIProps?: (id: string, props: any, field: string) => void
    setWidgetProperties: (props: IWidgetProps) => void;
    sourceBrowser: SourceBrowser;
    addErrors: (errors: IErrorProps[]) => void
    removeErrors: (ids: string[]) => void
    removeAllErrors: () => void
    isMobile?: boolean
}

export interface IUIFieldProps<T extends IBaseProperty> {
    mode: IWidgetMode;
    fieldProps: T;
    uiProps: any;
    uxpContext: IContextProvider;

    // these props will be used in field containers
    // making these optional so it wont break existing widgets 
    designer?: IWDDesignModeProps
    contextData?: any,
    onChangeContextData?: (contextData: any) => void
    propPath: PropertyPath,
    dataKey?: string,
    droppableParentContainerId?: string,
    disableDrop?: boolean,
    onChangeFields?: (newFields: ISerializedUIField[]) => void
}

export type TCollectionAttributeDataTypeName = 'text' | 'number' | 'datetime' | 'boolean' | 'json'

export type TCollectionAttributeMetaTypeName = 'name' | 'email' | 'sensorvalue' | 'time' | 'date' | 'number' | 'text' | 'true/false' | 'file' | 'notes' | 'position' | 'daterange' | 'card-widget'



export interface ICollectionAttributeTypeDefinition {
    metaType: TCollectionAttributeMetaTypeName,
    label: string,
    dataType: TCollectionAttributeDataTypeName,
}

export const AvailableAttributeMetaTypeDefinitions: ICollectionAttributeTypeDefinition[] = [
    {
        metaType: 'name',
        dataType: 'text',
        label: 'Name',
    },
    {
        metaType: 'email',
        dataType: 'text',
        label: 'Email',
    },
    {
        metaType: 'number',
        dataType: 'number',
        label: 'Number',
    },
    {
        metaType: 'date',
        dataType: 'datetime',
        label: 'Date',
    },
    {
        metaType: 'time',
        dataType: 'datetime',
        label: 'Time',
    },
    {
        metaType: 'text',
        dataType: 'text',
        label: 'Text',
    },
    {
        metaType: 'notes',
        dataType: 'text',
        label: 'Long Text',
    },
    {
        metaType: 'true/false',
        dataType: 'boolean',
        label: 'True/False',
    },
    {
        metaType: 'file',
        dataType: 'text',
        label: 'File',
    },
    {
        metaType: 'sensorvalue',
        dataType: 'number',
        label: 'Sensor Value',
    },
    {
        metaType: 'position',
        dataType: 'json',
        label: 'Current Position',
    },
    {
        metaType: 'daterange',
        dataType: 'json',
        label: 'Date Range',
    },
]

export function findDataTypeForAttributeMetaType(metaType: TCollectionAttributeMetaTypeName) {
    return AvailableAttributeMetaTypeDefinitions?.find((a) => a.metaType == metaType)?.dataType
}

export interface IUIFieldDefinition<T extends IBaseProperty = IBaseProperty> {
    field: (props: any) => JSX.Element;
    id: string;
    description: string;
    defaultProps: T;
    isInputField?: boolean,
    propertyEditor: (props: IPropertyEditorProps<T>) => JSX.Element;
    // only for input fields
    type?: TCollectionAttributeMetaTypeName,
    onDropField?: (field: ISerializedUIField) => ISerializedUIField
}

let fieldRegister: { [id: string]: IUIFieldDefinition<IBaseProperty> } = {};

export function registerUIField<T extends IBaseProperty>(field: IUIFieldDefinition<T>) {
    if (fieldRegister[field.id]) fieldRegister[`${field.id}###${field.description}`] = field
    else fieldRegister[field.id] = field;
}

export const getEditorFields = () => fieldRegister;
export function getUIFields() {
    return Object.values(fieldRegister);
}
export function getUIFieldMap() {
    let x: { [id: string]: (props: any) => JSX.Element } = {};
    for (let k in fieldRegister) {
        x[k] = fieldRegister[k].field;
    }
    return x;
}

export function getFieldByIdOrKey(field: ISerializedUIField, registeredFields: { [id: string]: IUIFieldDefinition<IBaseProperty> }) {
    if (hasValue(field?.fieldKey) && registeredFields[field?.fieldKey]) return registeredFields[field?.fieldKey]
    return registeredFields[field?.type]
}

export interface IUIField<T extends IBaseProperty = IBaseProperty> {
    type: string;
    props: IUIFieldProps<T>;
}
export interface ISerializedUIField {
    id: string
    name: string
    type: string;
    fieldKey?: string
    props: any;
    isInputField?: boolean,
    metaType?: TCollectionAttributeMetaTypeName,
    template?: any,
    droppableContainerId?: string,
    dataKey?: string,
    propPath?: PropertyPath,
    disableDrop?: boolean,
    onChangeFields?: (newFields: ISerializedUIField[]) => void
}
// export interface IUIFieldProps<T extends IBaseProperty> {
//     mode: IWidgetMode;
//     fieldProps: T;
// }


// export interface IWidgetTemplateProps<T = any> {
//     uxpContext?: IContextProvider;
//     designer: IWDDesignModeProps;
//     uiProps?: T;
//     params?: T
// }

export interface IWidgetTemplateInstanceProps {
    beforeSave?: (isNew: boolean, key?: string, modelKey?: string) => boolean | Promise<boolean | { canSave: boolean, updatedProps: any }>,
    onPreview?: () => boolean
}


export type PropertyPath = string | number | Array<string | number>;

function getProps(props: any, path: PropertyPath, isMobile?: boolean, elementId?: string) {
    if (!props) return null;

    if (isMobile && hasValue(elementId)) {
        const _field = props['fields'].find((f: any) => f.id == elementId)
        if (!_field) return null
        if (!_field.props) return null
        return _field.props.uiProps
    }
    return props
}
export function extractPath(props: any, path: PropertyPath, isMobile?: boolean, elementId?: string) {
    // let _props = getProps(props, path, isMobile, elementId)

    if (!props) return null;

    if (path instanceof Array) {
        let p = props;
        for (let i = 0; i < path.length; i++) {
            p = p[path[i]];
            if (!p) return null;
        }
        return p;
    }
    return props[path];
}

export function addToPath(path: PropertyPath, ...value: Array<string | number>) {
    if (path instanceof Array) {
        return [...path, ...value];
    }
    return [path, ...value];
}
export function applyAtPath(props: any, path: string, value: () => any) {
    let newProps = Object.assign({}, props);
    let parts = path.split('.').filter(x => (x != '$') && !!x);
    if (parts.length == 1) {
        newProps[parts[0]] = value();
        return newProps;
    }
    let currentPos = newProps;
    for (let part of parts.slice(0, -1)) {
        let v = currentPos[part];
        if (typeof v == 'undefined') {
            currentPos[part] = {};
        }
        currentPos = currentPos[part];
    }
    currentPos[parts[parts.length - 1]] = value();
    return newProps;
}
export function injectAtPath(props: any, path: PropertyPath, value: any) {
    if (!props) return null;
    if (!(path instanceof Array)) {
        path = [path];
    }
    if (path instanceof Array) {
        let p = props;
        for (let i = 0; i < path.length - 1; i++) {
            p = p[path[i]];
            if (!p) return null;
        }
        p[path[path.length - 1]] = value;
        return p;
    }
    throw 'Invalid path: ' + JSON.stringify([props, path, value]);
}

export function buildPath(path: PropertyPath) {
    try {
        if (!(path instanceof Array)) {
            path = [path]
        }
        let stringPath = jp.stringify(path as jp.PathComponent[])
        return stringPath
    } catch (error) {
        return null
    }
}



export interface IWidgetTemplateProps<T = any> {
    uxpContext?: IContextProvider;
    designer: IWDDesignModeProps;
    uiProps?: T;
    parentUIProps?: T
    params?: T
    initialSource: string;
    updateWidgetHeight?: (height: number) => void
    instanceId?: string,// this will be used to update the props of child widgets in mobile app,
    
    [key:string]: any; //individual params

    index?: number // not using this anymore 

    // these props will be used in field containers
    // making these optional so it wont break existing widgets 
    contextData?: any,
    onChangeContextData?: (contextData: any) => void
    propPath?: PropertyPath,
    dataKey?: string,
    droppableParentContainerId?: string,
    disableDrop?: boolean,
    onChangeFields?: (newFields: ISerializedUIField[]) => void
    isInnerField?: boolean

}
export declare type TemplateRenderer = (props: IWidgetTemplateProps) => JSX.Element;
export interface ILayoutProps {
    w?: number,
    h?: number,
    minW?: number,
    minH?: number,
    maxH?: number,
    maxW?: number
}

export interface IWidgetDataSourceConfig {
    dataSourceBindingsFieldName?: string; // Default is 'source' from uiProps
    dataSourcesFieldName?: string; // Default is 'dataSources' from uiProps
    supportRows?: boolean;
    jsonPathAppendForRows?: string;
}

export interface IWidgetTemplate {
    template: TemplateRenderer;
    toolbar?: TemplateRenderer;
    id: string;
    name: string,
    description: string;
    icon?: IconProp | JSX.Element,
    canDragDropFields?: boolean,
    layout?: ILayoutProps,
    complexity?: 'easy' | 'medium' | 'hard' | 'advanced',
    preLoader?: IWidgetPreloader,
    expectedSchema?: TargetDataStructure,
    autoHieght?: boolean,
    getHeight?: () => number,
    defaultProps?: any,
    key?: string // for user templates,
    isDefaultTemplate?: boolean
    moduleId?: string
    onDropField?: (f: ISerializedUIField) => ISerializedUIField
}

let cardTemplateRegister: { [id: string]: IWidgetTemplate } = {};
let mobileCardTemplateRegister: { [id: string]: IWidgetTemplate } = {};

export function registerCardTemplate(template: IWidgetTemplate) {
    let id = getUrlFriendlyString(template.id)
    if (!template.icon) template.icon = ['fad', 'align-justify'] as IconProp
    cardTemplateRegister[id] = { ...template, id: id, isDefaultTemplate: true };

    registerWidget({
        id: id,
        widget: template.template,
        isDefaultTemplate: true, // mark this widget as a template
        configs: {
            layout: template.layout || { w: 10, h: 10 },
            props: [
                {
                    name: "uiProps",
                    label: "UI",
                    type: "json"
                }
            ],
            preLoader: template?.preLoader || 'default',
        },
        defaultProps: {
            uiProps: {},
        }

    });

}

(window as any).registerCustomWidgetTemplate = (template: IWidgetTemplate) => {
    let id = getUrlFriendlyString(template.id)
    if (!template.icon) template.icon = ['fad', 'align-justify'] as IconProp
    cardTemplateRegister[id] = { ...template, id: id, isDefaultTemplate: false };
}


export function getCardTemplates(expectedSchema?: any, exactMatch: boolean = false, query?: string, isCustomTemplates?: boolean) {
    // console.log('card templates', cardTemplateRegister, expectedSchema);
    const allCards = { ...cardTemplateRegister }
    let templates = [...Object.values(allCards)]
    templates = filterTemplates(templates, expectedSchema, exactMatch, query)

    const isDefaultTemplate = isCustomTemplates != undefined ? !isCustomTemplates : true

    const final = templates.filter(t => (t.isDefaultTemplate == isDefaultTemplate))

    // console.log('after filtered', templates);
    return final;
}

export function containsText(source: string, search: string) {
    return (source?.indexOf(search) !== -1)
}
export function filterTemplates(templates: IWidgetTemplate[], expectedSchema?: any, exactMatch: boolean = false, query?: string) {
    // filter by schema 
    console.log('custom_widgets templates_____', templates)
    let _templates = [...templates]

    if (!!expectedSchema) {
        _templates = _templates.filter(t => {
            try {
                let path = deriveJSONPath(expectedSchema, {}, t.expectedSchema)
                if (exactMatch) {
                    return path == '$';
                }
                if (!!path) return true
            } catch (error) { }
            return false
        })
    }

    // filter by query 
    if (hasValue(query)) {
        _templates = _templates.filter(f => {
            return (containsText(f.id, query) || containsText(f.name, query) || containsText(f.description, query))
        })
    }
    return _templates
}

export function findCardTemplate(id: string) {
    // extract template details 
    let templateId = id
    if (id?.startsWith('user-template')) {
        const parts = id.split('@@@')
        templateId = parts[1]
    }

    let inst = cardTemplateRegister[templateId]
    if (inst) return inst
    return null
}

export function getUserTemplateKeyFromId(id: string) {
    if (id?.startsWith('user-template')) {
        const parts = id.split('@@@')
        return parts[2] || null
    }
    return null
}

export function extractModuleIdFromURL() {
    const searchParams = new URLSearchParams(window.location.search)
    if (!searchParams.has('module')) return
    const moduleId = searchParams.get('module')
    return moduleId
}
export async function extractModuleIdFromURLAndLoadScript() {

    const moduleId = extractModuleIdFromURL()
    // load script file 
    if (moduleId == 'uxp-widget-designer-bundle') return
    await loadScriptFileAsync(`/api/UXP/module?id=${moduleId}`, moduleId)
}



export function registerMobileCardTemplate(template: IWidgetTemplate) {
    let id = getUrlFriendlyString(template.id)
    if (!template.icon) template.icon = ['fad', 'align-justify'] as IconProp
    mobileCardTemplateRegister[id] = { ...template, id: id, isDefaultTemplate: true };

    registerWidget({
        id: id,
        widget: template.template,
        isDefaultTemplate: true, // mark this widget as a template
        configs: {
            layout: template.layout || { w: 10, h: 10 },
            props: [
                {
                    name: "uiProps",
                    label: "UI",
                    type: "json"
                }
            ],
            preLoader: template?.preLoader || 'default'
        },
        defaultProps: {
            uiProps: {},
        }
    });
}


export function getMobileCardTemplates(expectedSchema?: any, exactMatch: boolean = false, query?: string, isCustomTemplates?: boolean) {
    // console.log('card templates', cardTemplateRegister, expectedSchema);
    const allCards = { ...mobileCardTemplateRegister }
    let templates = [...Object.values(allCards)]
    templates = filterTemplates(templates, expectedSchema, exactMatch, query)

    const isDefaultTemplate = isCustomTemplates != undefined ? !isCustomTemplates : true
    const final = templates.filter(t => (t.isDefaultTemplate == isDefaultTemplate))
    return final;
}
export interface IWidgetInstance {
    Key: string,
    ID: string,
    Name: string,
    Icon: string,
    Description: string,
    NotificationTitle: string,
    NotificationBody: string,
    BackgroundImage: string,
    Anonymous: string,
    Template: {
        __template__: {
            id: string,
            type: string,
            layout?: ILayoutProps,
            zoom?: number,
            locked: boolean
        },
        uiProps: any
    },
    WidgetID: string
}
export function findAvailableFieldsInSchema(schema: any, path: string, results: any) {
    let targetSchema = (schemaAtJsonPath(schema, path));
    if (targetSchema?.type == 'array') {
        if (targetSchema.items?.type == 'object') {
            let required = Object.keys(targetSchema.items?.properties || {});
            for (let r of required) {
                results[r] = '1';
            }
        }
    }
    if (targetSchema?.type == 'object') {
        let required = Object.keys(targetSchema.properties || {});
        for (let r of required) {
            results[r] = '1';
        }
    }
}

export function findAvailableFields(item: IDataSourceBinding, sources: IDataSource[], results: any) {
    let source = sources?.find(x => x.id == item?.source);
    if (source) {
        let path = item?.path;
        return findAvailableFieldsInSchema(source.schema, path, results);
    }
};

export function validateUIProp(validator: () => boolean, errorId: string, containerId: string, fieldId: string, errorMessage: string, designer: IWDDesignModeProps, errorType: IErrorProps['type'] = 'property', showError = true) {
    if (validator()) {
        if (showError) {
            designer?.addErrors?.([{
                id: errorId,
                type: errorType,
                error: errorMessage,
                containerId: containerId,
                fieldId: fieldId
            }])
        }

        return false
    }
    designer?.removeErrors?.([errorId])
    return true
}

export interface IWidgetJSON {
    type: string,
    id: string,
    layout: {
        w: number,
        h: number
    },
    uiProps: { [key: string]: any }
}

export function generateWidgetFromJSON(widgetJson: IWidgetJSON, uxpContext: IContextProvider) {
    // find the template 
    let template = findCardTemplate(widgetJson.type)

    if (!template) {
        console.log("GenerateWidgetFromJSON:: widget template not-found for id", widgetJson.type);
        return null
    }

    return React.createElement(template.template as React.ForwardRefExoticComponent<React.RefAttributes<IWidgetTemplateInstanceProps> & IWidgetTemplateProps>,
        {
            designer: null,
            uiProps: widgetJson.uiProps,
            uxpContext: uxpContext,
            initialSource: null,
            // ref: templateRef,
            // updateWidgetHeight: updateWidgetHeight
        })
}